import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EntityDetails from "components/EntityDetails";
import ExamAttemptQuestionScore from "components/ExamAttemptQuestionScore";
import HTMLQuestion from "components/HTMLQuestion";
import { TableCellType } from "components/LazyTable";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SortableTable from "components/SortableTable";
import { isMultipleChoiceOrAnswersQuestionType } from "helpers/QuestionTypeHelpers";
import { makeStyles } from "helpers/StyleHelper";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import {
  EvaluationQuestionAnswer,
  ExamAttemptQuestion,
  Question,
} from "services/KnowfullyAdminClient";
import { dateToStandardFormat } from "utils/dateUtils";
import { ALPHABET } from "utils/evaluationQuestionUtils";
import ExamQuestionDetailsDialog from "./ExamQuestionDetailsDialog";
import EvaluationPreview from "components/EvaluationPreview";

const useStyles = makeStyles(() => ({
  question: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: "4",
    lineClamp: 4,
    WebkitBoxOrient: "vertical",
  },
  link: {
    cursor: "pointer",
    color: "blue",
    textDecoration: "underline",
  },
}));

const ExamAttemptDetails = () => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const {
    organizationUserService,
    userSkuService,
    userEvaluationService,
    examResultsUrl,
  } = useGlobalContext();

  const { userId, userSkuId, examAttemptId } = useParams<{
    userId: string;
    userSkuId: string;
    examAttemptId: string;
  }>();

  const [evaluationPrinting, setEvaluationPrinting] = useState(false);

  const [questionDetailsRow, setQuestionDetailsRow] = useState<
    ExamAttemptQuestion | undefined
  >();

  const [questionDetailsDialogOpen, setQuestionDetailsDialogOpen] = useState(
    false
  );

  const userResult = useQuery(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    }
  );

  const user = userResult?.data;
  const userName = getUserDisplayName(user, t({ id: "user" }));
  useTitle(t({ id: "examAttemptResultsTitle" }, { name: userName }));

  const userSkuResult = useQuery(
    ["getUserSku", userSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSkuId!
      ),
    { keepPreviousData: true, enabled: userResult.isFetched }
  );

  const userSku = userSkuResult?.data?.data!;

  const examAttemptDetailsQuery = useQuery(
    ["exam-attempt-details", examAttemptId],
    () => userEvaluationService.getExamAttemptDetails(examAttemptId!),
    { keepPreviousData: true }
  );

  const examAttemptDetails = examAttemptDetailsQuery?.data?.data;

  const entityDetailsRows = [
    RowElement("userNameLabel", userName!, userResult.isLoading),
    RowElement("courseName", userSku?.title ?? "", userSkuResult.isLoading),
    RowElement(
      "examNameLabel",
      examAttemptDetails?.examName ?? "-",
      examAttemptDetailsQuery.isLoading,
      <>
        <span>{examAttemptDetails?.examName ?? "-"}</span>
        {examAttemptDetails?.examName && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEvaluationPrinting(true);
            }}
          >
            <VisibilityIcon />
          </IconButton>
        )}
      </>
    ),
    RowElement(
      "submittedDate",
      !!examAttemptDetails?.attemptDate
        ? dateToStandardFormat(examAttemptDetails.attemptDate)
        : "-",
      examAttemptDetailsQuery.isLoading
    ),
    RowElement(
      "scoreLabel",
      !!examAttemptDetails?.score ? examAttemptDetails.score + "%" : "-",
      examAttemptDetailsQuery.isLoading
    ),
    RowElement(
      "passingScoreLabel",
      !!examAttemptDetails?.passingScore
        ? examAttemptDetails.passingScore + "%"
        : "-",
      examAttemptDetailsQuery.isLoading
    ),
    RowElement(
      "statusLabel",
      examAttemptDetails?.score && examAttemptDetails.passingScore
        ? examAttemptDetails.score >= examAttemptDetails.passingScore
          ? t({ id: "pass" })
          : t({ id: "fail" })
        : "-",
      examAttemptDetailsQuery.isLoading
    ),
  ].filter((r) => !!r.component);

  const questions = (examAttemptDetails?.questions ?? []).map((x, i) => {
    return { id: i.toString(), ...x };
  });

  const cells: Array<TableCellType> = [
    {
      accessor: "sequence",
      align: "center",
      width: 0,
      type: "text",
      label: t({ id: "sequenceLabel" }),
      noSort: true,
    },
    {
      accessor: "title",
      align: "left",
      width: 0,
      type: "node",
      View: ({ value }: { value: ExamAttemptQuestion }) => (
        <Box sx={styles.question}>
          <HTMLQuestion question={{ value: value.title! }} />
        </Box>
      ),
      label: t({ id: "questionLabel" }),
      noSort: true,
    },
    {
      accessor: "questionType.name",
      align: "center",
      width: 0,
      type: "node",
      View: ({ value }: { value: ExamAttemptQuestion }) => (
        <T
          id="questionTypeShort"
          values={{ type: value.questionType?.name ?? "-" }}
        />
      ),
      label: t({ id: "questionsTypeLabel" }),
      noSort: true,
    },
    {
      accessor: "score",
      align: "center",
      width: 0,
      type: "node",
      View: ({ value }: { value: ExamAttemptQuestion }) => (
        <ExamAttemptQuestionScore question={value} />
      ),
      label: t({ id: "scoreLabel" }),
      noSort: true,
    },
    {
      accessor: "userAnswer",
      align: "center",
      width: 0,
      type: "node",
      View: ({ value }: { value: ExamAttemptQuestion }) =>
        isMultipleChoiceOrAnswersQuestionType(
          value.questionType?.value ?? ""
        ) ? (
          <span>
            {value.userAnswers
              ?.map((y) => {
                return ALPHABET[
                  value.answers?.findIndex((x) => x.id === y.id)!
                ];
              })
              .join(",")}
          </span>
        ) : (
          <Box component="span" sx={styles.link}>
            <T id="seeMoreLabel" />
          </Box>
        ),
      label: t({ id: "userAnswerLabel" }),
      noSort: true,
    },
    {
      accessor: "correctAnswer",
      align: "center",
      width: 0,
      type: "node",
      View: ({ value }: { value: ExamAttemptQuestion }) =>
        isMultipleChoiceOrAnswersQuestionType(
          value.questionType?.value ?? ""
        ) ? (
          <span>
            {value.answers
              ?.reduce((answerIndexes: number[], answer, index) => {
                if (answer.isAnswer) {
                  answerIndexes.push(index);
                }
                return answerIndexes;
              }, [])
              .map((x) => ALPHABET[x])
              .join(",")}
          </span>
        ) : null,
      label: t({ id: "correctAnswerLabel" }),
      noSort: true,
    },
  ];

  const onClick = (id: string, row: ExamAttemptQuestion) => {
    setQuestionDetailsRow(row);
    setQuestionDetailsDialogOpen(true);
  };

  return (
    <>
      <RedirectBackButton
        redirectRoute={examResultsUrl(userId, userSkuId)}
        labelKey="backToExamResults"
      />

      <h1>{t({ id: "examAttemptResults" })}</h1>

      <EntityDetails
        details={entityDetailsRows}
        title={t({ id: "examAttemptDetails" })}
      />

      {questionDetailsRow && (
        <ExamQuestionDetailsDialog
          onClose={() => {
            setQuestionDetailsDialogOpen(false);
            setQuestionDetailsRow(undefined);
          }}
          open={questionDetailsDialogOpen}
          examAttemptQuestion={questionDetailsRow}
        />
      )}

      <SortableTable
        {...{
          rows: questions,
          dense: true,
          title: t({ id: "evaluationFormQuestionsLabel" }),
          cells,
          onClick,
        }}
        defaultOrderBy="" //Do not sort the table by default
        useRowIndexesAsReactKey={true}
      />
      {examAttemptDetails?.questions && (
        <EvaluationPreview
          title={examAttemptDetails.examName ?? ""}
          questions={examAttemptDetails.questions.map(
            (x) =>
              ({
                questionType: x.questionType,
                sequence: x.sequence,
                value: x.title,
                evaluationQuestionAnswers: x.answers?.map(
                  (y) =>
                    ({ id: y.id, value: y.value } as EvaluationQuestionAnswer)
                ),
              } as Question)
          )}
          onClose={() => setEvaluationPrinting(false)}
          open={evaluationPrinting}
        />
      )}
    </>
  );
};

export default ExamAttemptDetails;
